import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ViewNav } from "@/components/ViewNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ClipboardList, Loader2, Plus, User, Phone, Mail, Ruler, Clock,
  CheckCircle2, BellRing, XCircle, Trash2, CalendarIcon, Globe, StickyNote,
} from "lucide-react";
import { Brand } from "@/components/Brand";
import { TenantFields, EMPTY_TENANT, todayYMD } from "@/components/TenantFields";

const toYmd = (d) => {
  if (!d) return "";
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

// move-in within the next 3 days (and not past) for an active/notified entry
const moveInSoon = (e) => {
  if (!e.desired_date || !["active", "notified"].includes(e.status)) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(e.desired_date + "T00:00:00");
  const diff = (d - today) / 86400000;
  return diff >= 0 && diff <= 3;
};

const LOCATION_OPTS = ["Otorohanga", "Razza", "Te Kuiti"];

const countdown = (ymd) => {
  if (!ymd) return "";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const n = Math.round((new Date(ymd + "T00:00:00") - today) / 86400000);
  if (n < 0) return `${Math.abs(n)}d overdue`;
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  return `in ${n} days`;
};
const SITE_OPTS = [
  { key: "ALL", label: "All sites" },
  { key: "OTOROHANGA", label: "Otorohanga" },
  { key: "RAZZA", label: "Razza" },
  { key: "TE KUITI", label: "Te Kuiti" },
];

const TIME_SLOTS = [
  "08:00 AM", "08:30 AM",
  "09:00 AM", "09:30 AM",
  "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM",
  "01:00 PM", "01:30 PM",
  "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM",
  "04:00 PM", "04:30 PM",
  "05:00 PM", "05:30 PM"
];

const AssignDialog = ({ entry, onDone }) => {
  const [open, setOpen] = useState(false);
  const parts = (entry.customer_name || "").trim().split(/\s+/);
  const initialForm = () => ({
    ...EMPTY_TENANT,
    first_name: parts[0] || "", surname: parts.slice(1).join(" ") || "",
    phone: entry.phone || "", email: entry.email || "", move_in_date: todayYMD(),
  });
  const [form, setForm] = useState(initialForm);
  const [reserve, setReserve] = useState(false);
  const [siteFilter, setSiteFilter] = useState(entry.location ? entry.location.toUpperCase() : "ALL");
  const [onlyPreferred, setOnlyPreferred] = useState(true);
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) { setForm(initialForm()); setReserve(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    api.getUnits({ status: "vacant", site: siteFilter }).then((list) => {
      let l = list;
      if (onlyPreferred && entry.preferred_size) {
        const n = (s) => (s || "").toLowerCase();
        const filtered = list.filter((u) => n(u.size) === n(entry.preferred_size));
        if (filtered.length) l = filtered;
      }
      setUnits(l);
      setUnitId((cur) => (l.find((u) => u.id === cur) ? cur : (l[0]?.id || "")));
    }).catch(() => setUnits([]));
  }, [open, siteFilter, onlyPreferred, entry.preferred_size]);

  const assign = async () => {
    if (!unitId) { toast.error("Select an available shed"); return; }
    if (reserve && !form.move_in_date) { toast.error("Pick the move-in date to reserve until"); return; }
    setBusy(true);
    try {
      await api.assignWaitlist(entry.id, { unit_id: unitId, ...form, reserve, reserved_until: reserve ? form.move_in_date : "" });
      toast.success(reserve ? "Shed reserved" : "Shed assigned — unit now occupied");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign shed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`waitlist-assign-${entry.id}`}>
          Assign Shed
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-auto" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign a Shed — {entry.customer_name}</DialogTitle>
          <DialogDescription>Confirm the customer's details and pick an available shed. This occupies the unit and marks the waitlist entry matched.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Site</Label>
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger data-testid="assign-site"><SelectValue /></SelectTrigger>
                <SelectContent>{SITE_OPTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Available Shed</Label>
              <Select value={unitId} onValueChange={setUnitId}>
                <SelectTrigger data-testid="assign-unit"><SelectValue placeholder={units.length ? "Select shed" : "None available"} /></SelectTrigger>
                <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.unit_number} · {u.size || "—"} · {u.site_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox data-testid="assign-only-preferred" checked={onlyPreferred} onCheckedChange={(v) => setOnlyPreferred(!!v)} />
            <span className="text-sm text-slate-600">Only show sheds matching preferred size ({entry.preferred_size})</span>
          </label>
          <TenantFields form={form} set={set} prefix="assign" />
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox data-testid="assign-reserve" checked={reserve} onCheckedChange={(v) => setReserve(!!v)} />
            <span className="text-sm text-slate-600">Reserve until the move-in date (hold without occupying yet)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="assign-submit" onClick={assign} disabled={busy || !unitId} className="bg-green-600 hover:bg-green-700">{busy ? "Saving..." : (reserve ? "Reserve Shed" : "Assign & Occupy")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const STATUS = {
  active: { label: "Active", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock },
  notified: { label: "Notified", cls: "bg-amber-100 text-amber-700 border-amber-300", icon: BellRing },
  matched: { label: "Matched", cls: "bg-green-100 text-green-700 border-green-300", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", cls: "bg-red-50 text-red-600 border-red-200", icon: XCircle },
};

const StatPill = ({ label, value, tone, active, onClick }) => (
  <button
    data-testid={`waitlist-stat-${label.toLowerCase()}`}
    onClick={onClick}
    className={cn(
      "bg-white border rounded-lg px-4 py-3 text-left transition-all hover:shadow-md",
      active ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"
    )}
  >
    <p className={cn("font-display text-2xl font-bold font-mono-nums leading-none", tone)}>{value}</p>
    <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{label}</p>
  </button>
);

const EMPTY_ENQUIRY = { customer_name: "", email: "", phone: "", preferred_size: "", flexible_sizing: false, desired_date: "", meeting_time: "", location: "" };

export const EnquiryDialog = ({ sizes, onDone, open: ctrlOpen, onOpenChange, initial }) => {
  const [innerOpen, setInnerOpen] = useState(false);
  const controlled = ctrlOpen !== undefined;
  const open = controlled ? ctrlOpen : innerOpen;
  const setOpen = controlled ? onOpenChange : setInnerOpen;
  const [form, setForm] = useState(EMPTY_ENQUIRY);
  const [dateObj, setDateObj] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && initial) {
      setForm({ ...EMPTY_ENQUIRY, ...initial });
      setDateObj(initial.desired_date ? new Date(initial.desired_date + "T00:00:00") : null);
    }
  }, [open, initial]);

  const pickDate = (d) => {
    setDateObj(d || null);
    set("desired_date", toYmd(d));
  };

  const resetForm = () => {
    setForm(EMPTY_ENQUIRY);
    setDateObj(null);
  };

  const submit = async () => {
    if (!form.customer_name.trim() || !form.preferred_size) {
      toast.error("Customer name and preferred size are required");
      return;
    }
    setBusy(true);
    try {
      const res = await api.submitEnquiry(form);
      if (res.matched) toast.success(res.message);
      else toast.info(res.message);
      setOpen(false);
      resetForm();
      onDone();
    } catch {
      toast.error("Failed to submit enquiry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
      <DialogTrigger asChild>
        <Button data-testid="new-enquiry-button" className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-1.5" /> New Enquiry
        </Button>
      </DialogTrigger>
      )}
      <DialogContent data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle>New Customer Enquiry</DialogTitle>
          <DialogDescription>Every enquiry is added to the FIFO waitlist. No shed is auto-reserved — staff assign a unit manually.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label>Customer Name *</Label>
            <Input data-testid="enquiry-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input data-testid="enquiry-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="enquiry-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Preferred Location</Label>
            <Select value={form.location || "any"} onValueChange={(v) => set("location", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="enquiry-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any location</SelectItem>
                {LOCATION_OPTS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Preferred Size *</Label>
            <Select value={form.preferred_size} onValueChange={(v) => set("preferred_size", v)}>
              <SelectTrigger data-testid="enquiry-size"><SelectValue placeholder="Select a size" /></SelectTrigger>
              <SelectContent>
                {sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Desired Move-in Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="enquiry-date-trigger"
                    className={cn("w-full justify-start font-normal", !form.desired_date && "text-slate-400")}
                  >
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {form.desired_date ? new Date(form.desired_date + "T00:00:00").toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" data-testid="enquiry-date-popover">
                  <Calendar
                    mode="single"
                    selected={dateObj}
                    onSelect={pickDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label>Meeting Time</Label>
              <select
                data-testid="enquiry-meeting-time"
                value={form.meeting_time || ""}
                onChange={(e) => set("meeting_time", e.target.value)}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No specific time</option>
                {TIME_SLOTS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer pt-1">
            <Checkbox data-testid="enquiry-flexible" checked={form.flexible_sizing} onCheckedChange={(v) => set("flexible_sizing", !!v)} />
            <span className="text-sm text-slate-700">Flexible on size (accept any available shed)</span>
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="enquiry-submit" onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700">
            {busy ? "Submitting..." : "Submit Enquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EnquiryDetailDialog = ({ entry, open, onOpenChange, sizes, onSaved }) => {
  const [form, setForm] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (entry) {
      setForm({
        customer_name: entry.customer_name || "", phone: entry.phone || "", email: entry.email || "",
        preferred_size: entry.preferred_size || "", location: entry.location || "", desired_date: entry.desired_date || "",
        meeting_time: entry.meeting_time || "",
      });
      setNotes(entry.notes_log || []);
      setNoteText("");
    }
  }, [entry]);

  if (!entry || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.updateWaitlist(entry.id, form); toast.success("Enquiry updated"); onSaved?.(); }
    catch { toast.error("Failed to save"); } finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try { const upd = await api.addWaitlistNote(entry.id, noteText.trim()); setNotes(upd.notes_log || []); setNoteText(""); toast.success("Note added"); onSaved?.(); }
    catch { toast.error("Failed to add note"); } finally { setBusy(false); }
  };

  const sorted = [...notes].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="enquiry-detail-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {entry.customer_name}
            {entry.source === "website" && <span className="rounded-full bg-indigo-100 text-indigo-700 border border-indigo-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase">Website</span>}
          </DialogTitle>
          <DialogDescription>Requested {new Date(entry.created_at).toLocaleString("en-NZ")} · status: {entry.status}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1 col-span-2"><Label>Customer Name</Label><Input data-testid="detail-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></div>
            <div className="space-y-1"><Label>Phone</Label><Input data-testid="detail-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="space-y-1"><Label>Email</Label><Input data-testid="detail-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="space-y-1"><Label>Preferred Size</Label>
              <Select value={form.preferred_size} onValueChange={(v) => set("preferred_size", v)}>
                <SelectTrigger data-testid="detail-size"><SelectValue placeholder="Size" /></SelectTrigger>
                <SelectContent>{sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Preferred Location</Label>
              <Select value={form.location || "any"} onValueChange={(v) => set("location", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="detail-location"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any location</SelectItem>{LOCATION_OPTS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Desired Move-in Date</Label><Input data-testid="detail-date" type="date" value={form.desired_date} onChange={(e) => set("desired_date", e.target.value)} /></div>
            <div className="space-y-1">
              <Label>Meeting Time</Label>
              <select
                data-testid="detail-meeting-time"
                value={form.meeting_time || ""}
                onChange={(e) => set("meeting_time", e.target.value)}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No specific time</option>
                {TIME_SLOTS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Customer Message Display */}
          {entry?.message && (
            <div className="space-y-1 col-span-2">
              <Label>Customer Message</Label>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                {entry.message}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            {entry.status !== "matched" && entry.status !== "cancelled" && <AssignDialog entry={entry} onDone={() => { onSaved?.(); onOpenChange(false); }} />}
            <Button data-testid="detail-save" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? "Saving..." : "Save changes"}</Button>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><StickyNote className="h-4 w-4 text-slate-500" /> Notes</p>
            <div className="flex gap-2 mb-3">
              <Textarea data-testid="detail-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note (call outcome, follow-up, etc.)" className="min-h-[60px]" />
              <Button data-testid="detail-note-add" onClick={addNote} disabled={busy || !noteText.trim()} variant="outline" className="shrink-0 self-end">Add</Button>
            </div>
            {sorted.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No notes yet.</p>
            ) : (
              <div className="space-y-2" data-testid="detail-notes-log">
                {sorted.map((n) => (
                  <div key={n.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                    <p className="text-sm text-slate-700 whitespace-pre-line">{n.text}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{n.author} · {new Date(n.created_at).toLocaleString("en-NZ")}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Waitlist() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detailEntry, setDetailEntry] = useState(null);

  const load = useCallback(async () => {
    const [list, st] = await Promise.all([api.getWaitlist(), api.getWaitlistStats()]);
    setEntries(list);
    setStats(st);
    setLoading(false);
  }, []);
  useEffect(() => { load(); api.getSizes().then(setSizes); }, [load]);

  const openDetail = async (e) => {
    setDetailEntry(e);
    if (e.source === "website" && !e.acknowledged) {
      try { await api.acknowledgeWaitlist(e.id); } catch { /* non-blocking */ }
      setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, acknowledged: true } : x)));
    }
  };

  const setStatus = async (id, status) => {
    await api.updateWaitlist(id, { status });
    toast.success(`Marked ${status}`);
    load();
  };
  const remove = async (id) => { await api.deleteWaitlist(id); toast.success("Removed"); load(); };

  const shown = filter === "all" ? entries : entries.filter((e) => e.status === filter);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Brand subtitle="377 units · 3 sites" />
          <ViewNav />
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 py-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-blue-600" /> Waitlist &amp; Enquiries
            </h2>
            <p className="text-sm text-slate-500 mt-1">FIFO waitlist. Every enquiry joins the queue (no shed is auto-reserved); freed sheds auto-notify the next in line.</p>
          </div>
          <EnquiryDialog sizes={sizes} onDone={load} />
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatPill label="Total" value={stats.total} tone="text-slate-900" active={filter === "all"} onClick={() => setFilter("all")} />
            <StatPill label="Active" value={stats.active} tone="text-slate-700" active={filter === "active"} onClick={() => setFilter("active")} />
            <StatPill label="Notified" value={stats.notified} tone="text-amber-600" active={filter === "notified"} onClick={() => setFilter("notified")} />
            <StatPill label="Matched" value={stats.matched} tone="text-green-600" active={filter === "matched"} onClick={() => setFilter("matched")} />
            <StatPill label="Cancelled" value={stats.cancelled} tone="text-red-500" active={filter === "cancelled"} onClick={() => setFilter("cancelled")} />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading waitlist...
          </div>
        ) : shown.length === 0 ? (
          <div className="text-center py-20 text-slate-400 bg-white border border-slate-200 rounded-xl">
            No waitlist entries{filter !== "all" ? ` with status "${filter}"` : ""}. Submit a new enquiry to get started.
          </div>
        ) : (
          <div className="w-full overflow-x-auto bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="waitlist-table">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50/60 text-left">
                  <th className="px-5 py-3 font-medium">#</th>
                  <th className="px-3 py-3 font-medium">Customer</th>
                  <th className="px-3 py-3 font-medium">Contact</th>
                  <th className="px-3 py-3 font-medium">Size</th>
                  <th className="px-3 py-3 font-medium">Move-in</th>
                  <th className="px-3 py-3 font-medium">Requested</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((e, i) => {
                  const S = STATUS[e.status] || STATUS.active;
                  const Icon = S.icon;
                  return (
                    <tr key={e.id} data-testid={`waitlist-row-${e.id}`} onClick={() => openDetail(e)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 cursor-pointer">
                      <td className="px-5 py-3 font-mono-nums text-slate-400">{i + 1}</td>
                      <td className="px-3 py-3">
                        <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                          <User className="h-3.5 w-3.5 text-slate-400" /> {e.customer_name}
                          {e.source === "website" && (
                            <span data-testid={`waitlist-source-website-${e.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                              <Globe className="h-2.5 w-2.5" /> Website
                            </span>
                          )}
                          {e.source === "website" && !e.acknowledged && (
                            <span data-testid={`waitlist-new-${e.id}`} className="rounded-full bg-red-500 text-white px-1.5 py-0.5 text-[10px] font-bold animate-pulse">NEW</span>
                          )}
                        </span>
                        {e.location && <span className="block text-[11px] text-slate-400 pl-5">{e.location}</span>}
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        <div className="flex flex-col gap-0.5 text-xs">
                          {e.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" /> {e.phone}</span>}
                          {e.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-slate-400" /> {e.email}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className="flex items-center gap-1 font-mono-nums font-semibold text-slate-700">
                          <Ruler className="h-3.5 w-3.5 text-slate-400" /> {e.preferred_size}
                          {e.flexible_sizing && <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">flexible</span>}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500 font-mono-nums" data-testid={`waitlist-movein-${e.id}`}>
                        {e.desired_date ? (
                          <span className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono-nums">{new Date(e.desired_date + "T00:00:00").toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}</span>
                            {e.meeting_time && <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded px-1 py-0.5">{e.meeting_time}</span>}
                            <span className="text-slate-400">({countdown(e.desired_date)})</span>
                            {moveInSoon(e) && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`waitlist-movein-soon-${e.id}`}>
                                <BellRing className="h-2.5 w-2.5" /> Soon
                              </span>
                            )}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500 font-mono-nums">{new Date(e.created_at).toLocaleDateString()}<br />{new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-3 py-3">
                        <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold", S.cls)}>
                          <Icon className="h-3 w-3" /> {S.label}
                        </span>
                        {e.matched_unit_number && (
                          <p className="text-xs text-green-700 font-mono-nums mt-1">→ {e.matched_unit_number}</p>
                        )}
                      </td>
                      <td className="px-5 py-3" onClick={(ev) => ev.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {(e.status === "active" || e.status === "notified") && (
                            <AssignDialog entry={e} onDone={load} />
                          )}
                          {e.status === "notified" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs border-green-300 text-green-700 hover:bg-green-50" data-testid={`waitlist-match-${e.id}`} onClick={() => setStatus(e.id, "matched")}>
                              Mark Matched
                            </Button>
                          )}
                          {(e.status === "active" || e.status === "notified") && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" data-testid={`waitlist-cancel-${e.id}`} onClick={() => setStatus(e.id, "cancelled")}>
                              Cancel
                            </Button>
                          )}
                          <button data-testid={`waitlist-delete-${e.id}`} onClick={() => remove(e.id)} className="text-slate-300 hover:text-red-500 p-1">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <EnquiryDetailDialog
        entry={detailEntry}
        open={!!detailEntry}
        onOpenChange={(o) => !o && setDetailEntry(null)}
        sizes={sizes}
        onSaved={load}
      />
    </div>
  );
}